import { useState } from "react";
import { motion } from "framer-motion";
import {
  Lightning, Envelope, Copy, Check, ArrowClockwise,
  Globe, SpinnerGap, CaretDown, CaretUp
} from "@phosphor-icons/react";
import AuditScoreChart from "@/components/AuditScoreChart";
import EmailPreview from "@/components/EmailPreview";

export default function ProspectDetail({ prospect, audit, emails, loading, onRunAudit, onGenerateEmail, activeCampaign }) {
  const [emailOptions, setEmailOptions] = useState({
    tone: "professional",
    sender_name: "",
    sender_company: "",
  });
  const [showOptions, setShowOptions] = useState(false);

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="prospect-detail">
      {/* Header */}
      <div className="bg-white border border-[--border] p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2
              className="text-2xl font-bold tracking-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              data-testid="prospect-detail-name"
            >
              {prospect.name}
            </h2>
            <div className="flex items-center gap-4 mt-2 text-sm text-[--text-secondary]">
              {prospect.website && (
                <a
                  href={prospect.website.startsWith("http") ? prospect.website : `https://${prospect.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-[--primary] transition-colors"
                  data-testid="prospect-website-link"
                >
                  <Globe size={14} /> {prospect.website}
                </a>
              )}
              {prospect.email && <span>{prospect.email}</span>}
              {prospect.zip_code && <span>ZIP: {prospect.zip_code}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {audit && (
              <ScoreDisplay score={audit.overall_score} />
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3">
        <button
          data-testid="run-audit-btn"
          onClick={onRunAudit}
          disabled={loading || !prospect.website}
          className="flex items-center gap-2 bg-[--primary] text-white px-5 py-2.5 text-sm font-medium hover:bg-[--primary-hover] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? <SpinnerGap size={16} className="animate-spin" /> : <Lightning size={16} weight="bold" />}
          {audit ? "Re-run Audit" : "Run SEO Audit"}
        </button>

        {audit && (
          <button
            data-testid="generate-email-btn"
            onClick={() => onGenerateEmail({ ...emailOptions, campaign_id: activeCampaign?.id || null })}
            disabled={loading}
            className="flex items-center gap-2 border-2 border-[--primary] text-[--primary] px-5 py-2.5 text-sm font-medium hover:bg-[--primary-light] transition-all active:scale-95 disabled:opacity-50"
          >
            {loading ? <SpinnerGap size={16} className="animate-spin" /> : <Envelope size={16} weight="bold" />}
            Generate Email{activeCampaign ? ` (${activeCampaign.name})` : ""}
          </button>
        )}

        {audit && (
          <button
            data-testid="toggle-email-options"
            onClick={() => setShowOptions(!showOptions)}
            className="p-2.5 border border-[--border] hover:bg-[--bg-muted] transition-colors text-[--text-secondary]"
          >
            {showOptions ? <CaretUp size={16} /> : <CaretDown size={16} />}
          </button>
        )}
      </div>

      {/* Email Options */}
      {showOptions && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="bg-white border border-[--border] p-4 overflow-hidden"
        >
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-[--text-muted] mb-1.5 block">
                Tone
              </label>
              <select
                data-testid="email-tone-select"
                value={emailOptions.tone}
                onChange={(e) => setEmailOptions({ ...emailOptions, tone: e.target.value })}
                className="w-full px-3 py-2 border border-[--border] bg-[--bg-hover] text-sm outline-none focus:border-[--primary]"
              >
                <option value="professional">Professional</option>
                <option value="friendly">Friendly</option>
                <option value="direct">Direct</option>
                <option value="casual">Casual</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-[--text-muted] mb-1.5 block">
                Your Name
              </label>
              <input
                data-testid="sender-name-input"
                value={emailOptions.sender_name}
                onChange={(e) => setEmailOptions({ ...emailOptions, sender_name: e.target.value })}
                placeholder="ChiroTracker"
                className="w-full px-3 py-2 border border-[--border] bg-[--bg-hover] text-sm outline-none focus:border-[--primary]"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-[--text-muted] mb-1.5 block">
                Company
              </label>
              <input
                data-testid="sender-company-input"
                value={emailOptions.sender_company}
                onChange={(e) => setEmailOptions({ ...emailOptions, sender_company: e.target.value })}
                placeholder="Optional"
                className="w-full px-3 py-2 border border-[--border] bg-[--bg-hover] text-sm outline-none focus:border-[--primary]"
              />
            </div>
          </div>
        </motion.div>
      )}

      {/* Audit Results */}
      {audit && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="audit-results"
        >
          <AuditScoreChart audit={audit} />
        </motion.div>
      )}

      {/* Generated Emails */}
      {emails && emails.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="email-results"
        >
          <EmailPreview emails={emails} prospectId={prospect.id} prospectEmail={prospect.email} />
        </motion.div>
      )}

      {/* No website warning */}
      {!prospect.website && (
        <div className="bg-[--warning-bg] border border-[--warning] p-4 text-sm text-[#B38F00]" data-testid="no-website-warning">
          <strong>No website URL.</strong> Add a website to this prospect to run an SEO audit.
        </div>
      )}
    </div>
  );
}

function ScoreDisplay({ score }) {
  let bg, text;
  if (score >= 70) { bg = "var(--success-bg)"; text = "var(--success)"; }
  else if (score >= 40) { bg = "var(--warning-bg)"; text = "#B38F00"; }
  else { bg = "var(--danger-bg)"; text = "var(--danger)"; }

  return (
    <div
      className="flex flex-col items-center justify-center w-16 h-16 border-2"
      style={{ borderColor: text, background: bg }}
      data-testid="audit-score-display"
    >
      <span className="text-2xl font-black leading-none" style={{ color: text, fontFamily: "'Cabinet Grotesk', sans-serif" }}>
        {score}
      </span>
      <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: text }}>
        /100
      </span>
    </div>
  );
}
